import {
  CustomWorkflowToolEnum,
  WorkflowTypeEnum,
  type IBuiltInWorkflowMeta,
  type ICustomWorkflowToolMeta,
} from '@torim-app/types';

/**
 * Display metadata for built-in workflows. The actual default whenToTrigger /
 * instructions / DOD live on the backend in ai-workflow-defaults.ts — this list
 * mirrors them only as a fallback before the first fetch and to populate the
 * card grid icons/colors.
 */
export const builtInWorkflowMetas: IBuiltInWorkflowMeta[] = [
  {
    type: WorkflowTypeEnum.BusinessInfo,
    name: 'מידע על העסק והשירותים',
    description: 'עונה על שאלות על השירותים, המחירים, שעות הפעילות והכתובת',
    icon: 'pi-info-circle',
    color: '#8864ee',
    defaultWhenToTrigger: 'הלקוח שואל על העסק או על השירותים — מחירים, משך, שעות פעילות, כתובת.',
    defaultInstructions: 'ענה בקצרה ורק לפי פרטי העסק. כשהלקוח מתעניין בשירות, הצע לקבוע תור.',
    defaultDefinitionOfDone: 'הלקוח קיבל תשובה לשאלה שלו.',
    toolsUsed: ['getBusinessInfo'],
  },
  {
    type: WorkflowTypeEnum.Book,
    name: 'קביעת תור',
    description: 'הבוט קובע ללקוחות תורים חדשים בעצמו',
    icon: 'pi-calendar-plus',
    color: '#22c55e',
    defaultWhenToTrigger: 'הלקוח מבקש לקבוע תור חדש.',
    defaultInstructions: 'בדוק זמינות, הצע שעות לפי נותן שירות, הצע שירותים משלימים, ולאחר אישור — קבע את התור.',
    defaultDefinitionOfDone: 'התור נקבע בהצלחה והלקוח קיבל אישור.',
    toolsUsed: ['checkAvailability', 'getBusinessInfo', 'getComplementaryServices', 'scheduleAppointment', 'addToWaitingList'],
  },
  {
    type: WorkflowTypeEnum.Reschedule,
    name: 'הזזת תור',
    description: 'מזיז תורים בהתאם למדיניות הביטולים',
    icon: 'pi-clock',
    color: '#3b82f6',
    defaultWhenToTrigger: 'הלקוח מבקש להזיז, לדחות או להעביר תור קיים.',
    defaultInstructions: 'מצא את תורי הלקוח, ודא איזה הוא רוצה להזיז, הצע שעות חלופיות, והזז את התור לאחר אישור.',
    defaultDefinitionOfDone: 'התור הוזז והלקוח קיבל אישור על השינוי.',
    toolsUsed: ['findCustomerAppointments', 'checkAvailability', 'rescheduleAppointment'],
  },
  {
    type: WorkflowTypeEnum.Cancel,
    name: 'ביטול תור',
    description: 'מבטל תורים בהתאם למדיניות הביטולים',
    icon: 'pi-times-circle',
    color: '#ef4444',
    defaultWhenToTrigger: 'הלקוח מבקש לבטל תור קיים.',
    defaultInstructions: 'מצא את תורי הלקוח, ודא איזה לבטל, ובטל לאחר אישור מפורש.',
    defaultDefinitionOfDone: 'התור בוטל והלקוח קיבל אישור.',
    toolsUsed: ['findCustomerAppointments', 'cancelAppointment'],
  },
];

export const builtInMetaByType: Record<WorkflowTypeEnum, IBuiltInWorkflowMeta | undefined> = {
  [WorkflowTypeEnum.BusinessInfo]: builtInWorkflowMetas[0],
  [WorkflowTypeEnum.Book]: builtInWorkflowMetas[1],
  [WorkflowTypeEnum.Reschedule]: builtInWorkflowMetas[2],
  [WorkflowTypeEnum.Cancel]: builtInWorkflowMetas[3],
  [WorkflowTypeEnum.Custom]: undefined,
};

/** Tools the owner can grant to a custom workflow. */
export const customWorkflowToolMetas: ICustomWorkflowToolMeta[] = [
  {
    tool: CustomWorkflowToolEnum.GetBusinessInfo,
    name: 'מידע על העסק',
    description: 'הבוט יוכל לשתף מחירים, שעות פעילות ורשימת שירותים.',
    icon: 'pi-info-circle',
  },
  {
    tool: CustomWorkflowToolEnum.RequestPhotos,
    name: 'בקשת תמונות',
    description: 'הבוט יבקש מהלקוח לשלוח תמונות (אזורי טיפול, סוג שיער וכו׳).',
    icon: 'pi-camera',
  },
  {
    tool: CustomWorkflowToolEnum.FindClosestService,
    name: 'הצעת מחיר משוערת',
    description: 'הבוט ימצא את השירות הקרוב ביותר במחירון העסק לתיאור של הלקוח, וייתן מחיר משוער.',
    icon: 'pi-shekel',
  },
  {
    tool: CustomWorkflowToolEnum.NotifyOwner,
    name: 'התראה לבעל העסק',
    description: 'הבוט יוכל לסכם את השיחה ולשלוח התראה לבעל העסק לטיפול.',
    icon: 'pi-bell',
  },
  {
    tool: CustomWorkflowToolEnum.HandoffToHuman,
    name: 'העברה לאדם',
    description: 'הבוט יוכל להעביר את השיחה לבן אדם ולהשתיק את עצמו ל-24 שעות.',
    icon: 'pi-user',
  },
];

export const customWorkflowToolMetaByTool: Record<CustomWorkflowToolEnum, ICustomWorkflowToolMeta> =
  Object.fromEntries(customWorkflowToolMetas.map(m => [m.tool, m])) as Record<CustomWorkflowToolEnum, ICustomWorkflowToolMeta>;
